/**
 * Enquiry Form Modal
 *
 * Opens a modal overlay that contains the Shopify Forms app block.
 * On first open, the app form block is moved from its inline location into the modal
 * and its original container is hidden permanently.
 */
class EnquiryFormModal extends HTMLElement {
  connectedCallback() {
    this.modal = this.querySelector('[data-enquiry-modal]');
    this.openBtn = this.querySelector('[data-enquiry-open]');
    this.closeBtn = this.querySelector('[data-enquiry-close]');
    this.formContainer = this.querySelector('[data-enquiry-form-container]');

    /** @type {HTMLElement|null} */
    this.appFormBlock = null;
    this.formMoved = false;

    if (this.openBtn) {
      this.openBtn.addEventListener('click', () => this.open());
    }
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }

    // Close on backdrop click (only on the backdrop itself, not the modal content)
    if (this.modal) {
      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal) this.close();
      });
    }

    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });

    // Hide the inline Shopify Forms block on page load
    this.hideInlineForm();
  }

  get isOpen() {
    return this.modal && !this.modal.classList.contains('hidden');
  }

  /**
   * Find the Shopify Forms app block on the page.
   */
  findAppFormBlock() {
    if (this.appFormBlock) return this.appFormBlock;

    // Look for the Shopify Forms app block by its app URL pattern in the block id
    const candidates = document.querySelectorAll('[id*="forms"]');
    for (const el of candidates) {
      if (el.id.includes('shopify') && el.id.includes('forms')) {
        this.appFormBlock = /** @type {HTMLElement} */ (el);
        return this.appFormBlock;
      }
    }

    // Fallback: look for the Shopify Forms iframe or form element
    const shopifyForm =
      document.querySelector('.shopify-block [data-shopify-form]') ||
      document.querySelector('.shopify-block form[data-form-id]') ||
      document.querySelector('[id*="apps--forms"]');

    if (shopifyForm) {
      this.appFormBlock = /** @type {HTMLElement} */ (
        shopifyForm.closest('.shopify-block') || shopifyForm
      );
      return this.appFormBlock;
    }

    return null;
  }

  /**
   * Hide the inline Shopify Forms block so it only shows inside the modal.
   */
  hideInlineForm() {
    const appForm = this.findAppFormBlock();
    if (appForm) {
      appForm.style.display = 'none';
    }
  }

  open() {
    if (!this.modal || !this.formContainer) return;

    // Move the form into the modal on first open
    if (!this.formMoved) {
      const appForm = this.findAppFormBlock();
      if (appForm) {
        this.formContainer.appendChild(appForm);
        appForm.style.display = '';
        this.formMoved = true;
      }
    }

    this.modal.classList.remove('hidden');
    document.body.style.overflow = 'hidden';
  }

  close() {
    if (!this.modal) return;
    this.modal.classList.add('hidden');
    document.body.style.overflow = '';
  }
}

if (!customElements.get('enquiry-form-modal')) {
  customElements.define('enquiry-form-modal', EnquiryFormModal);
}
